<script setup lang="ts">
import { ref } from 'vue';
import { I18N } from 'core';

export interface ModalConfirmExpose {
  open: () => void;
  close: () => void;
}

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  titleType: {
    type: String,
    default: 'normal',
  },
  text: {
    type: String,
    default: '',
  },
  position: {
    type: String,
    default: 'bottom',
  },
  mainClassname: {
    type: String,
    default: '',
  },
  confirmText: {
    type: String,
    default: I18N.translate('Fleet_Drivers_Confirmation_jXFy', '确认'),
  },
  cancelText: {
    type: String,
    default: I18N.translate('Fleet_Drivers_Cancel_YjzD', '取消'),
  },
  hiddenCancel: {
    type: Boolean,
    default: false,
  },
  confirmAutoClose: {
    type: Boolean,
    default: true,
  },
  confirmDisable: {
    type: Boolean,
    default: false,
  },
  topIconName: {
    require: false,
    type: String,
    default: '',
  },
  leftIconName: {
    require: false,
    type: String,
    default: '',
  },
});

const emit = defineEmits(['confirm', 'cancel', 'close']);

const showLangModal = ref(false);

const onConfirm = () => {
  if (props.confirmAutoClose) {
    showLangModal.value = false;
  }
  emit('confirm');
};

const onCancel = () => {
  showLangModal.value = false;
  emit('cancel');
};

defineExpose<ModalConfirmExpose>({
  open: () => {
    showLangModal.value = true;
  },
  close: () => {
    showLangModal.value = false;
  },
});
</script>

<template>
  <van-popup
    v-model:show="showLangModal"
    round
    :position="(position as any)"
    :class="$style.comfirm_popup_modal"
    @close="emit('close')"
  >
    <div :class="[$style.main, mainClassname]">
      <div
        v-if="props.topIconName"
        :class="$style.top_icon_box"
      >
        <i
          :class="['icon', props.topIconName]"
        />
      </div>
      <div
        v-if="props.title"
        :class="[$style.title, $style[props.titleType]]"
      >
        {{ props.title }}
      </div>
      <div
        :class="$style.close"
        @click.stop="onCancel"
      >
        <i class="icon close" />
      </div>
      <div :class="$style.content_box">
        <div
          v-if="props.leftIconName"
          :class="$style.left_icon_box"
        >
          <i
            :class="['icon', props.leftIconName]"
          />
        </div>
        <div
          v-if="props.text"
          :class="$style.text"
        >
          {{ props.text }}
        </div>
      </div>
      <slot />
      <div :class="$style.button_bar">
        <van-button
          type="primary"
          class="large"
          :loading="confirmDisable"
          @click.stop="onConfirm"
        >
          {{ props.confirmText }}
        </van-button>
        <van-button
          v-if="!hiddenCancel"
          type="default"
          class="large"
          @click.stop="onCancel"
        >
          {{ props.cancelText }}
        </van-button>
      </div>
    </div>
  </van-popup>
</template>

<style lang="less" module>
@import 'style/vars.less';

.comfirm_popup_modal {
  border-radius: 40px 40px 0px 0px !important;
}

.main {
  position: relative;
  padding: 32px;
}
.top_icon_box{
  width: 100%;
  margin: 28px 0 16px;
  text-align: center;
}

.title {
  padding: 0 16px;
  &.normal {
    font-size: 40px;
    color: #1F2933;
    font-weight: 700;
    margin-top: 40px;
    margin-bottom: 24px;
  }

  &.large {
    font-size: 48px;
    color: #333333;
    font-weight: 700;
    margin-top: 44px;
    margin-bottom: 24px;
  }
}

.content_box{
  padding: 0 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 80px;
  .left_icon_box{
    height: 100%;
    margin-right: 48px;
  }
  .text {
    font-size: 28px;
    color: #000000;
    line-height: 42px;
    font-weight: 400;
  }
}

.close {
  position: absolute;
  top: 20px;
  right: 20px;
}

.button_bar {
  > button {
    &:not(:last-child) {
      margin-bottom: 32px;
    }
  }
}
</style>
